import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button, FormControl, FormLabel, Stack, RadioGroup, Radio, FormControlLabel, Box } from '@mui/material';
import ReactSelect from 'react-select';
import Modal from 'components/_layouts/Modal';
import { FilterAlt } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { FieldStatus, FieldTypeEnum } from '../_types/fieldbank';
import useFieldCategories from '../_data_sources/useFieldCategories';

export type FieldBankFiltersParams = {
  search?: string | null;
  status?: FieldStatus | null;
  fieldTypes?: FieldTypeEnum[] | null;
  fieldCategoryIds?: number[] | null;
};

export type FieldBankFiltersProps = {
  params: FieldBankFiltersParams;
  updateParams: (updatedParams: FieldBankFiltersParams) => void;
  hideStatusFilter?: boolean; // Hide status filter for template builder
  showClearAll?: boolean; // Show clear all button for template builder
  onClearAll?: () => void; // Clear all filters callback
};

const FieldBankFilters: React.FC<FieldBankFiltersProps> = ({
  params,
  updateParams,
  hideStatusFilter = false,
  showClearAll = false,
  onClearAll,
}) => {
  const { currentOrganizationId } = useContext(AppContext);
  const { data: fieldCategories = [] } = useFieldCategories();
  const [showFilters, setShowFilters] = useState(false);
  const [status, setStatus] = useState<string>(params.status || 'ALL');
  const [fieldTypes, setFieldTypes] = useState<FieldTypeEnum[]>(params.fieldTypes || []);
  const [fieldCategoryIds, setFieldCategoryIds] = useState<number[]>(params.fieldCategoryIds || []);

  useEffect(() => {
    setStatus(params.status || 'ALL');
    setFieldTypes(params.fieldTypes || []);
    setFieldCategoryIds(params.fieldCategoryIds || []);
  }, [params]);

  const handleApplyFilters = () => {
    const newParams: FieldBankFiltersParams = {
      status: status === 'ALL' ? undefined : (status as FieldStatus),
      fieldTypes: fieldTypes.length > 0 ? fieldTypes : undefined,
      fieldCategoryIds: fieldCategoryIds.length > 0 ? fieldCategoryIds : undefined,
    };
    updateParams(newParams);
    setShowFilters(false);
  };

  const handleClearAll = () => {
    // Reset local state
    setStatus(hideStatusFilter ? 'Active' : 'ALL'); // Keep Active for template builder
    setFieldTypes([]);
    setFieldCategoryIds([]);

    // Update params - for template builder, maintain Active status
    const clearedParams: FieldBankFiltersParams = {
      status: hideStatusFilter ? 'Active' : undefined,
      fieldTypes: undefined,
      fieldCategoryIds: undefined,
    };
    updateParams(clearedParams);

    // Call the external clear callback if provided
    if (onClearAll) {
      onClearAll();
    }

    setShowFilters(false);
  };

  // Category options for react-select
  const categoryOptions = useMemo(
    () =>
      (fieldCategories ?? []).map((cat) => ({
        value: String(cat.id),
        label: cat.name,
        category: cat,
      })),
    [fieldCategories]
  );

  // Field type options for react-select
  const fieldTypeOptions = useMemo(() => {
    const fieldTypeValues: FieldTypeEnum[] = ['Text', 'Number', 'Boolean', 'Date', 'Time', 'SingleSelect', 'Lookup'];
    return fieldTypeValues.map((type) => ({
      value: type,
      label: t(`performanceReporting.fieldBank.fieldType.${type}`),
    }));
  }, []);

  // Selected category values for react-select
  const selectedCategoryValues = useMemo(() => {
    return categoryOptions.filter((x) => (fieldCategoryIds ?? []).map(String).includes(String(x.value)));
  }, [fieldCategoryIds, categoryOptions]);

  // Selected field type values for react-select
  const selectedFieldTypeValues = useMemo(() => {
    return fieldTypeOptions.filter((x) => (fieldTypes ?? []).includes(x.value));
  }, [fieldTypes, fieldTypeOptions]);

  // Check if there are any active filters to enable/disable clear all button
  const hasActiveFilters = useMemo(() => {
    return fieldTypes.length > 0 || fieldCategoryIds.length > 0;
  }, [fieldTypes.length, fieldCategoryIds.length]);

  return (
    <>
      <Button
        onClick={() => setShowFilters(true)}
        size='small'
        color='neutral'
        startIcon={<FilterAlt />}
        className='icon-only'
      />
      <Modal
        title={t('performanceReporting.fieldBank.list.filters.title')}
        size='sm'
        fullWidthActions={showClearAll}
        actions={
          showClearAll ? (
            // Template builder layout: Cancel on left, Clear all + Apply filters on right
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Button
                onClick={() => setShowFilters(false)}
                color='neutral'
              >
                {t('common.cancel')}
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={handleClearAll}
                  color='neutral'
                  disabled={!hasActiveFilters}
                >
                  {t('common.clearAll')}
                </Button>
                <Button
                  variant='contained'
                  onClick={handleApplyFilters}
                >
                  {t('performanceReporting.fieldBank.list.actions.applyFilters')}
                </Button>
              </Box>
            </Box>
          ) : (
            // Field bank list layout: Cancel + Apply filters on right
            <>
              <Button
                onClick={() => setShowFilters(false)}
                color='neutral'
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='contained'
                onClick={handleApplyFilters}
              >
                {t('performanceReporting.fieldBank.list.actions.applyFilters')}
              </Button>
            </>
          )
        }
        open={showFilters}
        onClose={() => setShowFilters(false)}
      >
        <Stack spacing={4}>
          {!hideStatusFilter && (
            <FormControl>
              <FormLabel>{t('performanceReporting.fieldBank.list.filters.status')}</FormLabel>
              <RadioGroup
                row
                value={status}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setStatus(event.target.value);
                }}
                sx={{
                  '& .MuiRadio-root': {
                    padding: '2px 8px',
                  },
                }}
              >
                <FormControlLabel
                  key='status_all'
                  value='ALL'
                  control={<Radio />}
                  label={t('common.all')}
                />
                <FormControlLabel
                  key='status_draft'
                  value='Draft'
                  control={<Radio />}
                  label={t('performanceReporting.fieldBank.status.Draft')}
                />
                <FormControlLabel
                  key='status_active'
                  value='Active'
                  control={<Radio />}
                  label={t('performanceReporting.fieldBank.status.Active')}
                />
                <FormControlLabel
                  key='status_inactive'
                  value='Inactive'
                  control={<Radio />}
                  label={t('performanceReporting.fieldBank.status.Inactive')}
                />
              </RadioGroup>
            </FormControl>
          )}

          <FormControl>
            <FormLabel>{t('performanceReporting.fieldBank.list.filters.fieldCategoryIds')}</FormLabel>
            <ReactSelect
              value={selectedCategoryValues}
              options={categoryOptions}
              placeholder={t('common.all')}
              isMulti={true}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
                input: (base) => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 0,
                  minHeight: 45,
                }),
              }}
              onChange={(newValue) => {
                if (Array.isArray(newValue)) {
                  setFieldCategoryIds(
                    newValue.map((x) => (typeof x === 'object' && 'value' in x ? Number(x.value) : 0))
                  );
                } else {
                  setFieldCategoryIds([]);
                }
              }}
            />
          </FormControl>

          <FormControl>
            <FormLabel>{t('performanceReporting.fieldBank.list.filters.fieldTypes')}</FormLabel>
            <ReactSelect
              value={selectedFieldTypeValues}
              options={fieldTypeOptions}
              placeholder={t('common.all')}
              isMulti={true}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
                input: (base) => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 0,
                  minHeight: 45,
                }),
              }}
              onChange={(newValue) => {
                if (Array.isArray(newValue)) {
                  setFieldTypes(newValue.map((x) => (typeof x === 'object' && 'value' in x ? x.value : '')));
                } else {
                  setFieldTypes([]);
                }
              }}
            />
          </FormControl>
        </Stack>
      </Modal>
    </>
  );
};

export default FieldBankFilters;
